import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Package, Zap, Grid2X2, TrendingDown } from "lucide-react";
import api from "@/lib/api";
import { emptyItem } from "@/components/OrderItemsEditor";

/**
 * Catalog-aware product picker for the internal NewOrder / EditOrder screens.
 *
 * Presents (a) an inline autocomplete search box AND (b) a full-catalog grid modal.
 * When a product is picked, its options + tier rate are auto-populated into a
 * standard OrderItemsEditor item shape (fully editable afterward).
 *
 * Props:
 *   onPick(item)     — receives the ready-to-append order item.
 *   onCustom()       — user clicked "Add custom product" (blank item).
 */
export default function CatalogPicker({ onPick, onCustom }) {
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [category, setCategory] = useState("");
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    api.get("/catalog", { params: { active_only: true } })
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]));
  }, []);

  // Close inline dropdown on outside click
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowResults(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const groups = useMemo(() => Array.from(new Set(products.map((p) => p.group).filter(Boolean))).sort(), [products]);

  const results = useMemo(() => {
    if (!q.trim()) return products.slice(0, 8);
    const needle = q.toLowerCase();
    return products.filter((p) =>
      (p.name || "").toLowerCase().includes(needle) ||
      (p.group || "").toLowerCase().includes(needle) ||
      (p.subtitle || "").toLowerCase().includes(needle)
    ).slice(0, 10);
  }, [q, products]);

  const filtered = useMemo(() => {
    let list = products;
    if (category) list = list.filter((p) => p.group === category);
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter((p) => (p.name || "").toLowerCase().includes(needle));
    }
    return list;
  }, [products, category, q]);

  const pick = async (p) => {
    setShowResults(false);
    setShowModal(false);
    setQ("");
    const built = await buildItemFromCatalog(p);
    onPick(built);
  };

  return (
    <div className="mb-4" ref={wrapRef}>
      <div className="flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            ref={inputRef}
            value={q}
            onFocus={() => setShowResults(true)}
            onChange={(e) => { setQ(e.target.value); setShowResults(true); }}
            placeholder="Search catalog: Standard Business Card, Flex Banner…"
            className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl pl-9 pr-3 py-2.5 text-sm outline-none bg-white"
            data-testid="catalog-search-input"
          />
          {showResults && results.length > 0 && (
            <div className="absolute z-30 mt-1 w-full bg-white border border-zinc-200 rounded-xl shadow-2xl max-h-96 overflow-auto"
                 data-testid="catalog-search-dropdown">
              {results.map((p) => {
                const tiers = (p.quantity_tiers || []).slice(0, 4);
                return (
                  <div key={p.id} className="border-b border-zinc-100 last:border-0">
                    <button
                      type="button"
                      onClick={() => pick(p)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-orange-50 text-left transition-colors"
                      data-testid={`catalog-suggest-${p.id}`}
                    >
                      <div className="w-10 h-10 rounded-lg bg-zinc-100 flex-shrink-0 overflow-hidden">
                        {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : <Package size={14} className="m-auto text-zinc-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{p.name}</div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{p.group || "General"}</div>
                      </div>
                      <div className="text-xs text-right">
                        <div className="font-mono font-bold text-[#FF6B00]">₹{Math.round(p.base_price || 0)}</div>
                        <div className="text-[9px] text-zinc-500">from</div>
                      </div>
                    </button>
                    {tiers.length > 1 && (
                      <div className="px-3 pb-2 flex items-center gap-1 flex-wrap">
                        <TrendingDown size={9} className="text-emerald-600" />
                        <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 mr-1">Bulk</span>
                        {tiers.map((t, i) => (
                          <span key={i} className="text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded font-mono">
                            {t.from_qty}+ · ₹{Number(t.unit_price).toFixed(2)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="btn-swiss btn-outline text-sm"
          data-testid="catalog-browse-btn"
        >
          <Grid2X2 size={14} /> Browse Catalog
        </button>
        <button
          type="button"
          onClick={onCustom}
          className="btn-swiss btn-outline text-sm"
          data-testid="add-custom-product-btn"
          title="Add a product that's not in the online catalog"
        >
          <Zap size={14} /> Custom Product
        </button>
      </div>
      <div className="text-[11px] text-zinc-500 mt-1.5">💡 Search auto-fills options + tier pricing. Everything remains editable after adding.</div>

      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)} data-testid="catalog-modal">
          <div className="bg-white rounded-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <div>
                <div className="overline text-[#FF6B00]">Website Catalog</div>
                <div className="font-display text-xl font-black tracking-tighter">Pick a product to add</div>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-zinc-100 rounded-full" data-testid="catalog-modal-close"><X size={16} /></button>
            </div>
            <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2 overflow-x-auto">
              <button
                onClick={() => setCategory("")}
                className={`px-3 py-1.5 text-xs font-bold rounded-full whitespace-nowrap ${!category ? "bg-[#FF6B00] text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
                data-testid="cat-chip-all"
              >All ({products.length})</button>
              {groups.map((g) => (
                <button
                  key={g}
                  onClick={() => setCategory(g)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-full whitespace-nowrap ${category === g ? "bg-[#FF6B00] text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
                  data-testid={`cat-chip-${g.replace(/\s+/g, "-").toLowerCase()}`}
                >{g}</button>
              ))}
            </div>
            <div className="px-5 pt-3">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Filter…"
                className="w-full border-2 border-zinc-200 focus:border-[#FF6B00] rounded-xl px-3 py-2 text-sm outline-none"
                data-testid="catalog-modal-search"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((p) => (
                <div
                  key={p.id}
                  className="border-2 border-zinc-200 rounded-xl overflow-hidden hover:border-[#FF6B00] hover:shadow-lg transition-all bg-white"
                  data-testid={`catalog-card-${p.id}`}
                >
                  <button type="button" onClick={() => pick(p)} className="w-full text-left">
                    <div className="aspect-video bg-zinc-100 overflow-hidden">
                      {p.image_url ? (
                        <img src={p.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-zinc-300"><Package size={26} /></div>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 truncate">{p.group || "General"}</div>
                      <div className="text-sm font-bold truncate">{p.name}</div>
                      <div className="mt-1 flex items-center justify-between text-xs">
                        <span className="font-mono font-black text-[#FF6B00]">₹{Math.round(p.base_price || 0)}</span>
                        <span className="text-[10px] text-emerald-700 font-bold">GST {p.gst_rate ?? 18}%</span>
                      </div>
                    </div>
                  </button>
                  {/* Bulk pricing waterfall — visible on every card */}
                  <TierWaterfall product={p} />
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="col-span-full text-center py-10 text-sm text-zinc-500">No products match your filter.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Inline tier waterfall — visualises bulk price breaks per product.
 * Rendered on every card in the "Browse Catalog" modal so staff can upsell
 * to bigger quantities with one glance.
 */
function TierWaterfall({ product }) {
  const tiers = (product.quantity_tiers || []).slice(0, 4);
  if (!tiers.length) return null;
  const first = Number(tiers[0]?.unit_price || product.base_price || 0);
  return (
    <div className="border-t border-zinc-100 bg-emerald-50/60 px-3 py-2" data-testid={`tiers-${product.id}`}>
      <div className="text-[9px] font-black uppercase tracking-wider text-emerald-700 mb-1 flex items-center gap-1">
        <TrendingDown size={10} /> Bulk pricing
      </div>
      <div className="grid grid-cols-2 gap-1">
        {tiers.map((t, i) => {
          const up = Number(t.unit_price || 0);
          const save = first > 0 ? Math.max(0, Math.round((1 - up / first) * 100)) : 0;
          return (
            <div key={i} className="flex items-baseline justify-between text-[10px] bg-white border border-emerald-200 rounded px-1.5 py-0.5">
              <span className="font-bold">{t.from_qty}+</span>
              <span className="font-mono font-black text-[#FF6B00]">₹{up.toFixed(2)}</span>
              {save > 0 && i > 0 && <span className="text-[8px] text-emerald-700 font-bold ml-1">−{save}%</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Build an OrderItemsEditor-compatible line from a catalog product.
 * - Runs price engine at the product's min-qty with default options → unit price (incl. GST).
 * - Encodes default options as: material = paper/GSM/material value, printing_type = side value,
 *   remaining options appended into product_name.
 * - Uses an addon "Rate (from catalog)" line for the total, marks GST as "included".
 * - Preserves hsn_code on the item (already respected by bridge + PDF).
 */
async function buildItemFromCatalog(p) {
  const item = emptyItem();
  // Defaults
  const defaults = {};
  const optDisplayBits = [];
  let material = "";
  let printing_type = "";
  for (const opt of p.options || []) {
    const d = (opt.values || []).find((v) => v.is_default) || (opt.values || [])[0];
    if (!d) continue;
    defaults[opt.key] = d.value;
    const k = (opt.key || "").toLowerCase();
    const l = (opt.label || "").toLowerCase();
    if (!material && (k.includes("paper") || k.includes("gsm") || k.includes("material") || l.includes("paper") || l.includes("gsm") || l.includes("material"))) {
      material = d.label || d.value;
    } else if (!printing_type && (k.includes("side") || l.includes("side") || k.includes("print") || l.includes("print"))) {
      printing_type = d.label || d.value;
    } else {
      optDisplayBits.push(`${opt.label}: ${d.label || d.value}`);
    }
  }

  const qty = Math.max(1, p.min_quantity || 1);

  // Ask the price engine for the tier + option total (GST-inclusive)
  let unitPrice = Number(p.base_price) || 0;
  try {
    const { data } = await api.post("/catalog/price", {
      product_id: p.id, quantity: qty, options: defaults,
    });
    unitPrice = Number(data?.unit_price || unitPrice);
  } catch {}

  const nameSuffix = optDisplayBits.length ? ` · ${optDisplayBits.join(" · ")}` : "";
  item.product_category = p.group || p.category || "General";
  item.product_name = `${p.name}${nameSuffix}`;
  item.material = material;
  item.printing_type = printing_type;
  item.quantity = qty;
  item.gst_rate = Number(p.gst_rate ?? 18);
  item.gst_mode = "included"; // catalog prices already include GST
  item.hsn_code = p.hsn_code || "4911";
  item.catalog_product_id = p.id;
  item.catalog_slug = p.slug;
  item.catalog_options = defaults;
  // Sizes empty (product is unit-based), Addons carries the rate line
  item.sizes = [];
  item.addons = [
    { name: `Rate (${p.name}, from catalog)`, quantity: qty, rate: unitPrice },
  ];
  return item;
}
